import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";
import type { abrechnungDetail, abrechnungVorschau, abrechnungenListe, kassenbuchListe, mietrechnungenListe, tagesabschlussBerechnen, Bankverbindung } from "../../worker/data/geld";

export type Vorschau = Awaited<ReturnType<typeof abrechnungVorschau>>;
export type AbrechnungZeile = Awaited<ReturnType<typeof abrechnungenListe>>[number];
export type AbrechnungDetail = Awaited<ReturnType<typeof abrechnungDetail>>;
export type MietrechnungZeile = Awaited<ReturnType<typeof mietrechnungenListe>>[number];
export type Kassenbuch = Awaited<ReturnType<typeof kassenbuchListe>>;
export type Tagesabschluss = Awaited<ReturnType<typeof tagesabschlussBerechnen>>;
export type { Bankverbindung };

export interface PeriodeInfo {
  art: "woche" | "monat";
  aktuell: { von: string; bis: string; name: string };
  vorherige: { von: string; bis: string; name: string };
  naechste: { von: string; bis: string; name: string };
  abrechenbar: boolean;
  mindestbetragCent: number;
  heute: string;
}

const K = ["laden", "geld"] as const;
function nachAenderung() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["laden"] });
}

export const usePeriode = (tag?: string) => useQuery({ queryKey: [...K, "periode", tag ?? ""], queryFn: () => rufe<PeriodeInfo>(api.api.laden.abrechnungen.periode.$get({ query: { tag } })) });
export const useVorschau = (von: string, bis: string) => useQuery({ queryKey: [...K, "vorschau", von, bis], queryFn: () => rufe<Vorschau>(api.api.laden.abrechnungen.vorschau.$get({ query: { von, bis } })), enabled: !!von && !!bis });
export const useAbrechnungen = (von?: string, bis?: string) => useQuery({ queryKey: [...K, "abrechnungen", von ?? "", bis ?? ""], queryFn: () => rufe<{ abrechnungen: AbrechnungZeile[] }>(api.api.laden.abrechnungen.$get({ query: { von: von || undefined, bis: bis || undefined } })) });
export const useAbrechnung = (id: string) => useQuery({ queryKey: [...K, "abrechnung", id], queryFn: () => rufe<AbrechnungDetail>(api.api.laden.abrechnungen[":id"].$get({ param: { id } })), enabled: !!id });
export const useMietrechnungen = (status?: "offen" | "verrechnet" | "bezahlt" | "storniert") => useQuery({ queryKey: [...K, "mietrechnungen", status ?? ""], queryFn: () => rufe<{ rechnungen: MietrechnungZeile[] }>(api.api.laden.mietrechnungen.$get({ query: { status } })) });
export const useKassenbuch = (von: string, bis: string) => useQuery({ queryKey: [...K, "kassenbuch", von, bis], queryFn: () => rufe<Kassenbuch>(api.api.laden.kassenbuch.$get({ query: { von, bis } })) });
export const useTagesabschluss = (tag: string) => useQuery({ queryKey: [...K, "tagesabschluss", tag], queryFn: () => rufe<Tagesabschluss>(api.api.laden.tagesabschluss.$get({ query: { tag } })) });
export const useBank = () => useQuery({ queryKey: [...K, "bank"], queryFn: () => rufe<{ bank: Bankverbindung | null }>(api.api.laden.bank.$get()) });

export function useAbrechnungenErstellen() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (v: { von: string; bis: string }) => rufe<{ erstellt: string[]; uebersprungen: number }>(api.api.laden.abrechnungen.erstellen.$post({ json: v })), onSuccess: nach });
}
export function useAbrechnungAktion() {
  const nach = nachAenderung();
  return useMutation({
    mutationFn: async (v: { id: string; aktion: "senden" | "auszahlung"; daten?: { art: "bar" | "ueberweisung"; betragCent?: number; unterschriftPng?: string; notiz?: string } }): Promise<{ ok: true } | { auszahlungId: string }> => {
      const b = api.api.laden.abrechnungen[":id"];
      if (v.aktion === "senden") return rufe<{ ok: true; mail: boolean }>(b.senden.$post({ param: { id: v.id } }));
      return rufe<{ auszahlungId: string }>(b.auszahlung.$post({ param: { id: v.id }, json: { art: v.daten!.art, betragCent: v.daten?.betragCent, unterschriftPng: v.daten?.unterschriftPng, notiz: v.daten?.notiz ?? "" } }));
    },
    onSuccess: nach,
  });
}
export function useSchlussabrechnung() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (mieterId: string) => rufe<{ id: string }>(api.api.laden.mieter[":id"].schlussabrechnung.$post({ param: { id: mieterId } })), onSuccess: nach });
}
export function useMietrechnungAktion() {
  const nach = nachAenderung();
  return useMutation({
    mutationFn: (v: { id: string; aktion: "bezahlt" | "storno"; art?: "bar" | "ueberweisung"; grund?: string }) => (v.aktion === "bezahlt" ? rufe(api.api.laden.mietrechnungen[":id"].bezahlt.$post({ param: { id: v.id }, json: { art: v.art ?? "ueberweisung" } })) : rufe(api.api.laden.mietrechnungen[":id"].storno.$post({ param: { id: v.id }, json: { grund: v.grund ?? "" } }))),
    onSuccess: nach,
  });
}
export function useKassenbuchEintrag() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (v: { art: "einlage" | "entnahme" | "korrektur"; betragCent: number; beschreibung: string }) => rufe(api.api.laden.kassenbuch.$post({ json: v })), onSuccess: nach });
}
export function useTagesabschlussSpeichern() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (v: { tag: string; barIstCent: number }) => rufe(api.api.laden.tagesabschluss.$post({ json: v })), onSuccess: nach });
}
export function useBankSpeichern() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (v: Bankverbindung) => rufe(api.api.laden.bank.$put({ json: v })), onSuccess: nach });
}
export const useAuszahlungsanfragen = () => useQuery({ queryKey: [...K, "auszahlungsanfragen"], queryFn: () => rufe<{ anfragen: { id: string; mieterId: string; mieterName: string; mieternummer: string; betragCent: number; status: string; erstelltAt: string; notiz: string; abrechnungId: string | null }[] }>(api.api.laden.auszahlungsanfragen.$get()) });
export function useAuszahlungsanfrageAktion() {
  const nach = nachAenderung();
  return useMutation({
    mutationFn: (v: { id: string; aktion: "erledigen" | "ablehnen"; notiz?: string }) => (v.aktion === "erledigen" ? rufe<{ abrechnungId: string }>(api.api.laden.auszahlungsanfragen[":id"].erledigen.$post({ param: { id: v.id } })) : rufe(api.api.laden.auszahlungsanfragen[":id"].ablehnen.$post({ param: { id: v.id }, json: { notiz: v.notiz ?? "" } }))),
    onSuccess: nach,
  });
}
export const abrechnungPdfUrl = (id: string) => `/api/laden/abrechnungen/${id}/pdf`;
export const mietrechnungPdfUrl = (id: string) => `/api/laden/mietrechnungen/${id}/pdf`;
export const quittungPdfUrl = (id: string) => `/api/laden/auszahlungen/${id}/quittung.pdf`;
export const exportUrl = (art: string, von: string, bis: string) => `/api/laden/export/${art}?von=${von}&bis=${bis}`;

export const ABRECHNUNG_STATUS: Record<string, { badge: "wait" | "paid" | "neutral" | "attention"; text: string }> = {
  entwurf: { badge: "neutral", text: "Entwurf" },
  erstellt: { badge: "wait", text: "Auszahlung offen" },
  versendet: { badge: "wait", text: "Gesendet, Auszahlung offen" },
  ausgezahlt: { badge: "paid", text: "Ausgezahlt" },
  uebertragen: { badge: "neutral", text: "Übertrag" },
  forderung: { badge: "attention", text: "Forderung" },
  storniert: { badge: "neutral", text: "Storniert" },
};
